import React, { useEffect, useState } from "react";
import {
  Layout,
  Typography,
  Select,
  Card,
  Table,
  Tag,
  Modal,
  Descriptions,
  Button,
  Statistic,
  Row,
  Col,
  Spin,
  Switch,
} from "antd";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import { motion } from "framer-motion";
import axios from "axios";
import { BulbOutlined, BulbFilled } from "@ant-design/icons";

const { Header, Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

//const API_BASE = "http://localhost:3333";
const API_BASE = "http://165.22.220.143:3333";


function App() {
  const [data, setData] = useState([]);
  const [apps, setApps] = useState([]);
  const [selectedApp, setSelectedApp] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [selectedError, setSelectedError] = useState(null);
  const [isHealthy, setIsHealthy] = useState(true);
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    fetchApis();
    const interval = setInterval(fetchApis, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchApis = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/check`);
      const results = res.data.results;

      const tableData = Object.entries(results).flatMap(([appName, apis]) =>
        apis.map((api) => ({
          app: appName,
          api_name: api.api_name || "-",
          url: api.url,
          status: api.status,
          code: api.code,
          response_time: api.response_time,
          checked_at: api.checked_at,
          error: api.error || null,
        }))
      );

      setData(tableData);
      setApps(Object.keys(results));
      setIsHealthy(!tableData.some((a) => a.status !== "OK"));
    } catch (e) {
      console.error("Error fetching API data:", e);
      setIsHealthy(false);
    } finally {
      setLoading(false);
    }
  };

  const handleUrlClick = (record) => {
    if (record.status === "DOWN" || record.status === "FAILED") {
      setSelectedError(record);
      setErrorModalVisible(true);
    }
  };

  const filteredData =
    !selectedApp || selectedApp === "All"
      ? data
      : data.filter((item) => item.app === selectedApp);

  const columns = [
    { title: "Application", dataIndex: "app", key: "app" },
    { title: "API Name", dataIndex: "api_name", key: "api_name" },
    {
      title: "URL",
      dataIndex: "url",
      key: "url",
      render: (text, record) => (
        <span
          style={{
            color:
              record.status === "DOWN" || record.status === "FAILED"
                ? "#ad1210ff"
                : darkMode
                ? "#69b1ff"
                : "#1677ff",
            textDecoration:
              record.status === "DOWN" || record.status === "FAILED"
                ? "underline"
                : "none",
            cursor:
              record.status === "DOWN" || record.status === "FAILED"
                ? "pointer"
                : "default",
          }}
          onClick={() => handleUrlClick(record)}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => (
        <Tag
          color={status === "OK" ? "green" : status === "DOWN" ? "red" : "orange"}
        >
          {status}
        </Tag>
      ),
    },
    { title: "Code", dataIndex: "code" },
    { title: "Response Time (s)", dataIndex: "response_time" },
    { title: "Checked At", dataIndex: "checked_at" },
  ];

  const summaryByApp = apps.map((app) => {
    const appData = data.filter((d) => d.app === app);
    const upCount = appData.filter((d) => d.status === "OK").length;
    const downCount = appData.filter((d) => d.status !== "OK").length;
    return { app, upCount, downCount };
  });

  const COLORS = ["#52c41a", "#f5222d"];

  const bgColor = darkMode ? "#0d1117" : "#f4f7fb";
  const cardBg = darkMode ? "#161b22" : "#fff";
  const textColor = darkMode ? "#f0f0f0" : "#000";

  return (
    <Layout style={{ minHeight: "100vh", background: bgColor, transition: "0.4s" }}>
      {/* ===== HEADER ===== */}
      <Header
        style={{
          background: darkMode
            ? "linear-gradient(90deg, #0d1117, #161b22)"
            : "linear-gradient(90deg, #001529, #004080)",
          height: "110px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 30px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
          transition: "0.4s",
        }}
      >
        <Title
          level={3}
          style={{
            color: "#fff",
            letterSpacing: 0.5,
            fontWeight: 700,
            margin: 0,
          }}
        >
          ⚡ Schemax API Monitor Pro
        </Title>

        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          <Switch
            checkedChildren={<BulbFilled />}
            unCheckedChildren={<BulbOutlined />}
            checked={darkMode}
            onChange={(val) => setDarkMode(val)}
          />
          <Button
            type="default"
            style={{
              background: "#fff",
              borderRadius: 20,
              fontWeight: 600,
            }}
            onClick={fetchApis}
          >
            Refresh Data
          </Button>
        </div>
      </Header>

      {/* ===== CONTENT ===== */}
      <Content style={{ padding: "30px" }}>
        <Card
          style={{
            borderRadius: "16px",
            background: cardBg,
            color: textColor,
            boxShadow: darkMode
              ? "0 4px 25px rgba(255,255,255,0.1)"
              : "0 4px 25px rgba(0,0,0,0.1)",
            border: "none",
            transition: "0.4s",
          }}
        >
          {/* ===== FILTER ===== */}
          <div
            style={{
              marginBottom: 20,
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <Select
              showSearch
              placeholder="Select Application"
              value={selectedApp}
              onChange={(value) => setSelectedApp(value)}
              optionFilterProp="children"
              filterOption={(input, option) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
              style={{ width: 260 }}
              allowClear
            >
              <Option value="">All Applications</Option>
              {apps.map((app) => (
                <Option key={app} value={app}>
                  {app}
                </Option>
              ))}
            </Select>
          </div>

          {/* ===== SUMMARY CARDS ===== */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "20px",
              justifyContent: "center",
              marginBottom: 30,
            }}
          >
            {summaryByApp
              .filter(
                (item) =>
                  !selectedApp || selectedApp === "All" || item.app === selectedApp
              )
              .map((item, index) => {
                const pieData = [
                  { name: "Up", value: item.upCount },
                  { name: "Down", value: item.downCount },
                ];
                const isHealthyApp = item.downCount === 0;

                return (
                  <motion.div
                    key={item.app}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card
  hoverable
  bordered={false}
  style={{
    width: 320,
    borderRadius: 20,
    textAlign: "center",
    background: isHealthyApp
      ? "linear-gradient(135deg, #d6e4ff, #f0f5ff)"
      : "linear-gradient(135deg, #fff1f0, #ffe6e6)",
    boxShadow: isHealthyApp
      ? "0 6px 20px rgba(47,84,235,0.25)"
      : "0 6px 20px rgba(255,85,85,0.25)",
    transition: "all 0.3s ease-in-out",
    transform: "translateY(0)",
    cursor: "pointer",
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.transform = "translateY(-8px)";
    e.currentTarget.style.boxShadow = isHealthyApp
      ? "0 12px 30px rgba(47,84,235,0.4)"
      : "0 12px 30px rgba(255,85,85,0.4)";
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.transform = "translateY(0)";
    e.currentTarget.style.boxShadow = isHealthyApp
      ? "0 6px 20px rgba(47,84,235,0.25)"
      : "0 6px 20px rgba(255,85,85,0.25)";
  }}
>

                      <Title level={4} style={{ marginBottom: 10, color: textColor }}>
                        {item.app}
                      </Title>

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          margin: "15px 0",
                        }}
                      >
                        <PieChart width={160} height={160}>
                          <Pie
                            data={pieData}
                            dataKey="value"
                            innerRadius={50}
                            outerRadius={70}
                            paddingAngle={2}
                            cx="50%"
                            cy="50%"
                          >
                            {pieData.map((entry, idx) => (
                              <Cell
                                key={`cell-${idx}`}
                                fill={COLORS[idx % COLORS.length]}
                              />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </div>

                      <Row justify="space-around">
                        <Col>
                          <Statistic
                            title="UP"
                            value={item.upCount}
                            valueStyle={{ color: "#52c41a" }}
                          />
                        </Col>
                        <Col>
                          <Statistic
                            title="DOWN"
                            value={item.downCount}
                            valueStyle={{ color: "#cf1322" }}
                          />
                        </Col>
                      </Row>
                      <Text
                        style={{
                          display: "block",
                          marginTop: 10,
                          color: isHealthyApp ? "#52c41a" : "#cf1322",
                          fontWeight: 500,
                        }}
                      >
                        {isHealthyApp
                          ? "✅ All APIs Healthy"
                          : "⚠️ Issues Detected"}
                      </Text>
                    </Card>
                  </motion.div>
                );
              })}
          </div>

          <Spin spinning={loading} tip="Fetching API Status...">
            <Table
              dataSource={filteredData}
              columns={columns}
              pagination={{ pageSize: 10 }}
              rowKey={(r) => r.url + r.app}
              bordered
              size="middle"
              style={{ background: cardBg, color: textColor }}
            />
          </Spin>
        </Card>

        <Modal
          title="API Error Details"
          open={errorModalVisible}
          onCancel={() => setErrorModalVisible(false)}
          footer={null}
          centered
          bodyStyle={{
            background: darkMode ? "#1e2632" : "#fff",
            color: textColor,
          }}
        >
          {selectedError ? (
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Application">
                {selectedError.app}
              </Descriptions.Item>
              <Descriptions.Item label="API Name">
                {selectedError.api_name}
              </Descriptions.Item>
              <Descriptions.Item label="URL">
                {selectedError.url}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {selectedError.status}
              </Descriptions.Item>
              <Descriptions.Item label="Response Code">
                {selectedError.code || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Checked At">
                {selectedError.checked_at}
              </Descriptions.Item>
              <Descriptions.Item label="Error Message">
                <pre
                  style={{
                    whiteSpace: "pre-wrap",
                    background: "#fff1f0",
                    padding: "10px",
                    borderRadius: "5px",
                    color: "#a8071a",
                  }}
                >
                  {selectedError.error || "No error message available"}
                </pre>
              </Descriptions.Item>
            </Descriptions>
          ) : (
            <p>No error details available.</p>
          )}
        </Modal>
      </Content>
    </Layout>
  );
}

export default App;

